import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Truck, Store, MapPin, Clock, Loader2, CheckCircle2, XCircle, Phone, Navigation } from "lucide-react";
import { useData } from "@/context/DataContext";
import { api, money } from "@/lib/api";

export default function DeliveryPickupPage() {
  const { settings } = useData();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const zones = settings?.delivery_zones || [];
  const hoursSet = (settings?.hours || []).some((h) => h.text);
  const addr = settings?.address || "135 Congress St, Portland, ME 04101";
  const phone = settings?.phone || "207-773-8000";
  const digits = phone.replace(/[^0-9]/g, "");

  const check = async () => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const r = await api.post("/delivery/check", { query: q });
      setRes(r.data);
    } finally { setLoading(false); }
  };

  return (
    <div>
      <section className="relative py-20 bg-gradient-to-b from-emerald-950 to-[#101311] tropical-grain">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-6xl">Delivery & Pickup</h1>
          <p className="text-[#FDFBF7]/75 mt-4 text-lg">Fast pickup and Caribbean flavor delivered to your door across Southern Maine.</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-14 grid md:grid-cols-2 gap-6">
        <div className="rounded-3xl bg-[#181D1A] border border-emerald-900/60 p-7">
          <Store className="w-9 h-9 text-amber-400" />
          <h2 className="font-display font-black text-2xl uppercase mt-3">Pickup</h2>
          <p className="text-[#FDFBF7]/70 mt-2">Order ahead, choose your time, and grab your food fresh and hot. {settings?.pickup_enabled === false ? "Currently paused." : "Available now."}</p>
          <a href={`https://maps.google.com/?q=${encodeURIComponent(addr)}`} target="_blank" rel="noreferrer" data-testid="pickup-address" className="flex items-start gap-2 mt-4 text-[#FDFBF7]/90 hover:text-amber-300">
            <MapPin className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" /><span className="font-semibold">{addr}</span>
          </a>
          <div className="flex flex-col sm:flex-row gap-2 mt-5">
            <button onClick={() => navigate("/menu")} className="px-6 py-3 rounded-full bg-amber-400 text-[#0A2818] font-display font-black uppercase tracking-wide active:scale-95 transition-transform min-h-[48px]">Start Pickup Order</button>
            <a data-testid="call-for-pickup" href={`tel:+1${digits}`} className="px-6 py-3 rounded-full border border-amber-500/60 text-amber-300 hover:bg-emerald-900/50 font-display font-black uppercase tracking-wide flex items-center justify-center gap-2 active:scale-95 transition-transform min-h-[48px]"><Phone className="w-5 h-5" /> Call for Pickup</a>
          </div>
          <a data-testid="pickup-directions-button" href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}`} target="_blank" rel="noreferrer" className="mt-2 px-6 py-3 rounded-full bg-emerald-700 hover:bg-emerald-600 text-[#FDFBF7] font-display font-black uppercase tracking-wide flex items-center justify-center gap-2 active:scale-95 transition-transform min-h-[48px]"><Navigation className="w-5 h-5" /> Get Directions</a>
        </div>
        <div className="rounded-3xl bg-[#181D1A] border border-emerald-900/60 p-7">
          <Truck className="w-9 h-9 text-amber-400" />
          <h2 className="font-display font-black text-2xl uppercase mt-3">Delivery</h2>
          <p className="text-[#FDFBF7]/70 mt-2">We deliver across Southern Maine. Check your area below, then order online.</p>
          <div className="flex gap-2 mt-5">
            <input data-testid="delivery-zip-checker-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Enter ZIP or town" className="flex-1 bg-[#101311] rounded-full px-4 py-3 text-sm border border-emerald-800/50 outline-none focus:border-amber-400" />
            <button data-testid="delivery-zip-checker-submit" onClick={check} className="px-5 rounded-full bg-emerald-700 hover:bg-emerald-600 font-bold flex items-center gap-2">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}</button>
          </div>
          {res && (
            <div data-testid="delivery-check-result" className={`mt-3 flex items-start gap-2 text-sm ${res.available ? "text-emerald-400" : "text-red-400"}`}>
              {res.available ? <CheckCircle2 className="w-5 h-5 shrink-0" /> : <XCircle className="w-5 h-5 shrink-0" />}
              <span>{res.available ? `We deliver to ${res.town}! Fee ${money(res.fee)} · ${res.eta} · min order ${money(res.min_order)}.` : res.message}</span>
            </div>
          )}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-10">
        <h2 className="font-display font-black text-2xl uppercase mb-5">Current Delivery Areas</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {zones.map((z) => (
            <div key={z.zip} className="rounded-2xl bg-[#181D1A] border border-emerald-900/60 p-4">
              <div className="font-display font-bold">{z.town}</div>
              <div className="text-xs text-[#FDFBF7]/50">{z.zip} · fee {money(z.fee)}</div>
            </div>
          ))}
        </div>
        <p className="text-[#FDFBF7]/50 text-sm mt-4">…and surrounding areas. Areas and fees are updated by Sir. King Kitchen.</p>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-20">
        <h2 className="font-display font-black text-2xl uppercase mb-5 flex items-center gap-2"><Clock className="w-6 h-6 text-amber-400" /> Business Hours</h2>
        <div className="rounded-3xl bg-[#181D1A] border border-emerald-900/60 p-6 max-w-lg">
          {hoursSet ? (
            (settings?.hours || []).map((h) => (
              <div key={h.day} className="flex justify-between py-2 border-b border-emerald-900/40 last:border-0">
                <span className="font-semibold">{h.day}</span>
                <span className="text-[#FDFBF7]/70">{h.text || "Closed"}</span>
              </div>
            ))
          ) : (
            <p className="text-[#FDFBF7]/60">Business hours will be posted soon. Call {settings?.phone || "207-773-8000"} for today's availability.</p>
          )}
          {settings?.holiday_hours_note && <p className="text-amber-300 text-sm mt-3">{settings.holiday_hours_note}</p>}
        </div>
      </section>
    </div>
  );
}
